import React, {FormEvent, MutableRefObject, useImperativeHandle, useState} from "react";
import {observer} from "mobx-react-lite";
import intl from "react-intl-universal";
import {useAppState, useRootStore} from "@stores/AppStateContext";

import {TaskReportFileMeta, TaskReportModelType} from "../../../../stores/taskLists/TaskReportModel";
import type {CalendarTable} from "../../../screens/Lists/components/CalendarTable";
import {FileUploadsManager} from "../../../../stores/models/FileUploadsManager";
import {FileProvider} from "../../../../utils/file/FileProvider";

import {Button} from "../../../components/Button";
import {MultiSelectWorkspaceMembers} from "../../../components/SelectTypes/MultiSelectWorkspaceMembers";
import {DragDropUpload} from "../../../components/DragDropUpload";
import {Textarea} from "../../../components/form/Textarea";
import {AddIcon} from "../../../components/icons/AddIcon";
import {FlagIcon} from "../../../components/icons/FlagIcon";
import {FormHint} from "../../../components/FormHint";
import {InfoIcon} from "../../../components/icons/InfoIcon";
import {AttachFiles} from "../../../components/AttachFiles/AttachFiles";
import {useDropArea} from "../../../hooks/useDropArea";

import {supportedImageTypes} from "../../../../actions/import/importFile";

export interface CompleteTaskFormProps {
    modelRef: MutableRefObject<TaskReportModelType>;
    calendarTable: CalendarTable;
    taskId: string;
    onCantComplete: () => void;
    imagesFileUploadManager: FileUploadsManager<TaskReportFileMeta>;
    commentImagesFileUploadManager: FileUploadsManager<TaskReportFileMeta>;
    fileProvider: FileProvider;
}

export interface CompleteTaskFormHandle {
    handleClose: () => void;
}

export const CompleteTaskForm = observer(React.forwardRef<CompleteTaskFormHandle, CompleteTaskFormProps>((
    {
        modelRef,
        calendarTable,
        taskId,
        onCantComplete,
        imagesFileUploadManager,
        commentImagesFileUploadManager,
        fileProvider,
    }: CompleteTaskFormProps,
    ref
) => {
    const appState = useAppState();
    const rootStore = useRootStore();
    const taskReportModel = modelRef.current;
    const task = calendarTable.taskDs?.getTaskById(taskId);
    const taskName = task?.name;
    const taskListUnit = calendarTable.taskListUnitDS.getTaskListUnitById(taskReportModel.taskListUnitId);
    const taskListName = Object.values(taskListUnit?.taskLists ?? {}).find(list => list.id === task?.taskListId)?.name ?? intl.get('taskList.ungrouped.short').d('Ungrouped Tasks');

    const [isVisibleComment, setIsVisibleComment] = useState(
        Boolean(taskReportModel.comment) || (taskReportModel.commentImages?.length ?? 0) > 0
    );
    const [hasError, setError] = useState<string[]>([]);

    const {isDragOver, onDragEnter, onDragLeave, onDragOver, onDrop} = useDropArea((files) => imagesFileUploadManager.addFiles(files), supportedImageTypes);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const errors: string[] = [];

        if (!taskReportModel.completedBy || taskReportModel.completedBy.length === 0) {
            errors.push('noUsers');
        }

        if (task?.config?.isImageRequired && imagesFileUploadManager.entries.length === 0) {
            errors.push('noImages');
        }

        if (errors.length > 0) {
            setError(errors);
            return;
        }

        await imagesFileUploadManager.waitAll();
        await commentImagesFileUploadManager.waitAll();

        taskReportModel.setImages(imagesFileUploadManager.json);
        if (commentImagesFileUploadManager.json) {
            taskReportModel.setCommentImages(commentImagesFileUploadManager.json);
        }

        await taskReportModel.save();

        appState.setModal(null);
    };

    const handleClose = () => {
        imagesFileUploadManager.clear();
        commentImagesFileUploadManager.clear();
        appState.setModal(null);
    };

    const handleRemoveComment = () => {
        setIsVisibleComment(false);
        taskReportModel.setComment('');
        while (commentImagesFileUploadManager.entries.length > 0) {
            commentImagesFileUploadManager.removeFileAt(commentImagesFileUploadManager.entries.length - 1);
        }
    };

    useImperativeHandle(ref, () => ({ handleClose }));

    return (
        <form
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDragOver={onDragOver}
            onDrop={onDrop}
            className="TaskReportEdit__form"
            onSubmit={handleSubmit}
        >
            {isDragOver && (
                <div className="TaskEditDragDropUpload__overlay" />
            )}
            <div className="title-large-bold TaskReportEdit__ContentBlock__taskName">{taskName}</div>
            <div className="body-medium TaskReportEdit__ContentBlock__tasklListName">{taskListName}</div>
            <div className="TaskReportEdit__ContentBlock">
                <div className="title-medium TaskReportEdit__ContentBlock__header">
                    {intl.get('taskReportEdit.CompletedBy.header').d('Completed by')}
                    <span className="TaskReportEdit__requiredField">*</span>
                </div>
                <div className="body-medium TaskReportEdit__ContentBlock__description">
                    {intl.get('taskReportEdit.CompletedBy.description').d('Select the staff who performed this task.')}
                </div>
                <MultiSelectWorkspaceMembers
                    withLabel={false}
                    value={taskReportModel.completedBy}
                    onChange={(val) => {
                        taskReportModel.setCompletedBy(val);
                        setError(prev => prev.filter(e => e !== 'noUsers'));
                    }}
                    placeholder={intl.get('addMembers_taskReportEdit.select_users').d('Add members')}
                    targetWorkspaceId={rootStore!.view.workspace!.id}
                />
                {hasError.includes('noUsers') && (
                    <FormHint
                        type="error"
                        size="small"
                        icon={<InfoIcon size={16}/>}            
                        text={intl.get('taskReportEdit.noSelectedUser').d('No users selected. Please select who completed this task.')}
                        className="TaskReportEdit__FormHint"
                    />
                )}
            </div>
            {task?.config?.isImageRequired && (
                <div className="TaskReportEdit__ContentBlock">
                    <div className="title-medium TaskReportEdit__ContentBlock__header">
                        {intl.get('taskReportEdit.required-confirmation.header').d('Required Confirmation')}
                        <span className="TaskReportEdit__requiredField">*</span>
                    </div>
                    <div className="body-medium TaskReportEdit__ContentBlock__description">
                        {intl.get('taskReportEdit.required-confirmation.description').d('Upload the required photo/image.')}
                    </div>
                    <>
                        <DragDropUpload
                            fileUploadsManager={imagesFileUploadManager}
                            mode="image"
                            accept="image/*"
                            hint={intl.getHTML('taskReportEdit.imageUpload.in-box-hint', {
                                uploadBtnLink: `<button class='title-medium btn btn--inline btn--primary'>${intl.get('import_doc.upload-btn').d('Upload file')}</button>`
                            })}
                            subHint={intl.get('taskReportEdit.imageUploadFormats').d('Supported: .jpg, .png — Max file size: 4 MB')}
                        />
                        {hasError.includes('noImages') && (
                            <FormHint
                                type="error"
                                size="small"
                                icon={<InfoIcon size={16}/>}            
                                text={intl.get('taskReportEdit.noSelectedImage').d('Upload is required to mark this task as complete.')}
                                className="TaskReportEdit__FormHint"
                            />
                        )}
                    </>
                </div>
            )}
            {isVisibleComment ? (
                <div className="TaskReportEdit__ContentBlock">
                    <div className="title-medium TaskReportEdit__ContentBlock__header">
                        {intl.get('taskReportEdit.Comment.header').d('Comment')}
                        <Button
                            text={intl.get('taskReportEdit.remove.button').d('Remove')}
                            style="inline"
                            type="primary"
                            onClick={handleRemoveComment}
                            className="TaskReportEdit__removeButton"
                        />
                    </div>
                    <div className="body-medium TaskReportEdit__ContentBlock__description">
                        {intl.get('taskReportEdit.Comment.description').d('Add any notes or context, if needed.')}
                    </div>
                    <Textarea
                        withBorder
                        value={taskReportModel.comment || ''}
                        onChange={taskReportModel.setComment}
                        fixedHeight={120}
                    />
                    <AttachFiles
                        fileUploadsManager={commentImagesFileUploadManager}
                        fileProvider={fileProvider}
                        className="TaskReportEdit__AttachFileButton"
                    />
                </div>
            ) : (
                <div className="TaskReportEdit__ContentBlock">
                    <Button
                        text={intl.get('taskReportEdit.add-comment.button').d('Add Comment')}
                        style="inline"
                        type="primary"
                        Icon={AddIcon}
                        onClick={() => setIsVisibleComment(true)}
                        className="TaskReportEdit__addCommentButton"
                    />
                </div>
            )}
            <div className="TaskReportEdit__Buttons">
                <Button
                    Icon={FlagIcon}
                    text={intl.get('taskReportEdit.cant-complete-task.button').d("Can't Complete Task")}
                    style="soft"
                    type="neutral"
                    onClick={onCantComplete}
                />
                <Button
                    text={intl.get('taskReportEdit.mark-as-complete.button').d('Mark as complete')}
                    style="solid"
                    type="primary"
                    htmlType={"submit"}
                    loading={taskReportModel.isSaving}
                    className="TaskReportEdit__SaveButton"
                />
            </div>
        </form>
    );
}));

export default CompleteTaskForm;
